<template>
  <div class="flex flex-col h-full p-6">
    <!-- Title strip header -->
    <div
      v-if="title"
      class="flex-shrink-0 px-4 pt-4 pb-3 border-b border-white/10">
      <div class="flex items-center gap-3">
        <!-- Small poster -->
        <div class="w-10 overflow-hidden rounded flex-shrink-0">
          <img
            v-if="title.poster_path"
            :src="`https://image.tmdb.org/t/p/w92${title.poster_path}`"
            :alt="displayTitle"
            class="object-cover w-full" />
          <div
            v-else
            class="flex items-center justify-center w-full h-14 bg-gray-700">
            <fa-icon :icon="['far', 'film']" class="text-gray-500 text-xs" />
          </div>
        </div>
        <!-- Title info -->
        <div class="min-w-0 flex-1">
          <h3 class="text-sm font-medium text-white truncate">
            {{ displayTitle }}
          </h3>
          <div class="flex items-center gap-2 text-xs text-gray-400">
            <span v-if="releaseYear">{{ releaseYear }}</span>
            <span v-if="titleType" class="uppercase">{{ titleType }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Error state -->
    <div
      v-if="error"
      class="flex items-center p-3 mx-4 mt-4 text-xs text-red-500 rounded-md bg-red-500/10 border border-red-500/20">
      <fa-icon icon="triangle-exclamation" class="mr-2" />
      <p>{{ error }}</p>
    </div>

    <!-- Loading state -->
    <div
      v-if="loading && !currentNote && !isCreatingNewNote"
      class="flex-1 flex items-center justify-center p-8">
      <div class="animate-pulse text-gray-400">Loading note...</div>
    </div>

    <!-- Note form content -->
    <div v-else class="flex-1 px-4 pt-4 overflow-y-auto">
      <form id="note-form" class="space-y-4" @submit.prevent="submitNote">
        <div>
          <label
            class="block mb-2 text-xs font-medium text-gray-400 uppercase tracking-wide">
            Your Note..
          </label>
          <textarea
            v-model="noteContent"
            class="w-full p-3 text-sm text-white rounded-lg border border-gray-700 bg-gray-800 resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            :class="{
              'cursor-not-allowed bg-gray-900 opacity-60': !canEdit,
              'cursor-text': canEdit
            }"
            rows="5"
            :readonly="!canEdit"
            :disabled="!canEdit"
            placeholder="Add your thoughts about this title..."></textarea>
        </div>

        <p v-if="canEdit" class="text-sm text-gray-400">
          Only you can see this note.
        </p>
      </form>
    </div>

    <!-- Action buttons footer (1/3 - 2/3 split) -->
    <div
      v-if="canEdit"
      class="flex-shrink-0 p-4 border-t border-white/10 bg-gray-900/50">
      <div class="flex gap-2">
        <button
          type="button"
          class="flex-1 py-3 text-sm font-medium text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
          @click="closeModal">
          Cancel
        </button>
        <button
          type="submit"
          form="note-form"
          class="flex-[2] py-3 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="loading || !noteContent.trim()">
          {{
            loading ? 'Saving...' : isCreatingNewNote ? 'Add Note' : 'Save Note'
          }}
        </button>
      </div>
    </div>

    <!-- Read-only footer -->
    <div
      v-else
      class="flex-shrink-0 p-4 border-t border-white/10 bg-gray-900/50">
      <button
        type="button"
        class="w-full py-3 text-sm font-medium text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        @click="closeModal">
        Close
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useStoreNotes } from '@/stores/storeNotes'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreLists } from '@/stores/storeLists'

const emit = defineEmits(['close'])

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (value) =>
      ['list', 'list_item', 'review', 'title'].includes(value)
  },
  list_item: Object,
  list: Object,
  review: Object,
  title: Object,
  titleType: String
})

const storeProfile = useStoreProfile()
const storeNotes = useStoreNotes()
const storeLists = useStoreLists()

const loading = ref(false)
const error = ref('')
const isCreatingNewNote = ref(false)
const currentNote = ref(null)
const noteContent = ref('')

// Title display helpers
const displayTitle = computed(() => {
  return props.title?.title || props.title?.name || 'Unknown Title'
})

const releaseYear = computed(() => {
  const date = props.title?.release_date || props.title?.first_air_date
  if (date) {
    return new Date(date).getFullYear()
  }
  return null
})

// Get the object ID based on type
const objectId = computed(() => {
  switch (props.type) {
    case 'list':
      return props.list?.id
    case 'list_item':
      return props.list_item?.id
    case 'review':
      return props.review?.id
    case 'title':
      return props.title?.id
    default:
      return null
  }
})

// Check if current user can edit
const canEdit = computed(() => {
  // For new notes, any logged-in user can create a note on a visible list item
  if (isCreatingNewNote.value) {
    return !!storeProfile.currentUserId
  }
  // For existing notes, only the note owner can edit
  return currentNote.value?.user_id === storeProfile.currentUserId
})

// Load note when component mounts or when objectId changes
watch([() => props.type, objectId], fetchNote, { immediate: true })

async function fetchNote() {
  if (!objectId.value || !props.type) return

  loading.value = true
  error.value = ''
  isCreatingNewNote.value = false

  try {
    // Try to find existing notes for this object by current user
    const notes = await storeNotes.fetchNotesByObject(
      props.type,
      objectId.value
    )

    // Find the current user's note
    const userNote = notes?.find(
      (note) => note.user_id === storeProfile.currentUserId
    )

    if (userNote) {
      currentNote.value = userNote
      noteContent.value = userNote.content || ''
    } else {
      // No note found - we'll show an empty form
      currentNote.value = null
      noteContent.value = ''
      isCreatingNewNote.value = true
    }
  } catch (err) {
    error.value = 'Failed to load note: ' + (err.message || 'Unknown error')
  } finally {
    loading.value = false
  }
}

async function submitNote() {
  if (!noteContent.value.trim()) return

  loading.value = true
  error.value = ''

  try {
    if (isCreatingNewNote.value) {
      // Create new note. No is_private: the server writes every note private
      // and ignores the field.
      await storeNotes.createNote({
        object_type: props.type,
        [`${props.type}_id`]: objectId.value,
        content: noteContent.value.trim()
      })
    } else {
      // Update existing note
      await storeNotes.updateNote(currentNote.value.id, {
        content: noteContent.value.trim()
      })
    }

    // Refresh the list item to update notes
    if (props.type === 'list_item' && props.list_item?.id) {
      storeLists.fetchListItemById(props.list_item.id)
    }

    closeModal()
  } catch (err) {
    error.value = 'Failed to save note: ' + (err.message || 'Unknown error')
  } finally {
    loading.value = false
  }
}

function closeModal() {
  emit('close')
}
</script>
